// Own account security of RadioOS-managed users (documentation/protocol.md, managed authentication
// phase E). Session cookie + CSRF like every other mutation (installApiAuth). New recovery codes
// exist only in the caller's memory; nothing here touches browser storage.
import { apiRequest } from './tenancy';

export interface OwnSession {
  id: string;
  auth_method: string;
  amr?: string[];
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  user_agent?: string;
  current: boolean;
}

export interface AccountSecurity {
  account: {
    login_email: string;
    password_set_at?: string;
    mfa: { required: boolean; enrolled: boolean; recovery_codes_remaining: number };
  };
  current_amr: string[];
  sessions: OwnSession[];
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });
const base = '/api/auth/account';

export const getAccountSecurity = () => apiRequest<AccountSecurity>(base);
export const changePassword = (currentPassword: string, newPassword: string, code = '') =>
  apiRequest<{ status: string; other_sessions_revoked: number }>(`${base}/password`,
    json('POST', code ? { current_password: currentPassword, new_password: newPassword, code } : { current_password: currentPassword, new_password: newPassword }));
export const regenerateRecoveryCodes = (password: string, code: string) =>
  apiRequest<{ status: string; recovery_codes: string[] }>(`${base}/recovery-codes`, json('POST', { password, code }));
export const revokeSession = (id: string) => apiRequest<{ status: string; current: boolean }>(`${base}/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const revokeOtherSessions = () => apiRequest<{ status: string; sessions_revoked: number }>(`${base}/sessions/revoke-others`, { method: 'POST' });

/** How a session was established, in words. */
export function describeAMR(amr: string[] | undefined): string {
  const m = amr || [];
  const first = m.includes('entra') ? 'Microsoft' : m.includes('google') ? 'Google' : m.includes('pwd') ? 'Password' : '';
  if (!first) return 'Unknown';
  if (m.includes('otp')) return `${first} + authenticator app`;
  if (m.includes('recovery')) return `${first} + recovery code`;
  if (first === 'Password') return 'Password';
  return m.includes('mfa') ? `${first} (with MFA)` : `${first} (without MFA)`;
}

/** Whether a password change from this session also needs an authenticator code. */
export function passwordChangeNeedsCode(currentAMR: string[], enrolled: boolean): boolean {
  return enrolled && currentAMR.includes('recovery');
}
